import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../app/hooks';
import { fetchGlobalMagicSignList, associateMagicSignCompany, sendMagicSignReminder } from '../../common/MagicSign/MagicSignService';
import { GlobalMagicSignRow, AuditLogEvent, MagicSignFilters, MagicSignStatus } from '../../common/MagicSign/types';
import { GLOBAL_MAGIC_SIGN_PAGE_SIZE, GLOBAL_MAGIC_SIGN_TABS, GlobalMagicSignTab } from './constants';
import {
    deriveSigningLinks,
    buildUniqueUploadFileName,
    isReadyToGenerateLinks,
    MAGIC_SIGN_ALLOWED_TYPES,
    MAGIC_SIGN_MAX_FILE_SIZE_MB,
    MAGIC_SIGN_PDF_ONLY_TOAST,
} from '../../common/MagicSign/constants';
import useMagicSignCommonHandlers from '../../common/MagicSign/useMagicSignCommonHandlers';
import useMagicSignShared from '../../common/MagicSign/useMagicSignShared';
import { uploadMagicSignDocument } from '../../common/MagicSign/uploadMagicSignDocument';

interface UseGlobalMagicSignLogicOptions {
    /** Gates the initial fetch — set false while the drawer tab is hidden */
    isActive?: boolean;
}

const useGlobalMagicSignLogic = ({ isActive = true }: UseGlobalMagicSignLogicOptions = {}) => {
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const pnoId: string = platformData?.investorOrgId ?? '';
    // Current user — used to auto-apply a "my requests" filter like Magic Upload's uploader chip.
    // requestedByUserId is stamped server-side from the platform user id (getPlatformUserId), which
    // equals localStorage 'platform_user_id'. Do NOT fall back to 'user_id': that is a different id
    // space (the x-user-id header) and can never match requestedByUserId, which would silently
    // filter the entire list to empty.
    const userName: string = useAppSelector((state) => state.user?.userData?.userName) || 'My documents';
    const currentUserId: string = (typeof window !== 'undefined' && localStorage.getItem('platform_user_id')) || '';
    // categoryId mirrors useMnemosyneUpload: read from investor config staging bucket
    const investorConfig = useAppSelector((state) => state.investor.config);
    const stagingCategoryId: string | undefined = (() => {
        const id = investorConfig?.dataConfig?.staging?.categoryId;
        return id != null ? String(id) : undefined;
    })();

    const uploadingRowsRef = useRef<Set<string>>(new Set());

    const [activeTab, setActiveTab] = useState<GlobalMagicSignTab>(GLOBAL_MAGIC_SIGN_TABS.MAGIC_SIGN);
    const [rows, setRows] = useState<GlobalMagicSignRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageNo, setPageNo] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [activeRowId, setActiveRowId] = useState<string | null>(null);
    const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
    const [auditLogOpen, setAuditLogOpen] = useState(false);
    const [auditLogEvents, setAuditLogEvents] = useState<AuditLogEvent[]>([]);
    const [auditLogLoading, setAuditLogLoading] = useState(false);
    const [auditLogRowName, setAuditLogRowName] = useState('');
    const [docIdToView, setDocIdToView] = useState('');
    const [docNameToView, setDocNameToView] = useState('');
    // Auto-apply the current user's requests by default (mirrors Magic Upload's uploader filter)
    const [activeFilters, setActiveFilters] = useState<MagicSignFilters>(currentUserId ? { requestedBy: [currentUserId] } : {});

    // Stale-response guard (mirrors useCommentsPagination's requestTokenRef): a slow earlier
    // fetchList call must not clobber rows with stale data if a newer call has since resolved.
    const fetchRequestTokenRef = useRef(0);

    const fetchList = useCallback(
        async (page = 0, filters?: MagicSignFilters) => {
            if (!pnoId) return;
            const token = ++fetchRequestTokenRef.current;
            setLoading(true);
            try {
                const res = await fetchGlobalMagicSignList({
                    pnoId,
                    pageNo: page,
                    pageSize: GLOBAL_MAGIC_SIGN_PAGE_SIZE,
                    filters,
                });
                if (token !== fetchRequestTokenRef.current) return; // superseded by a newer fetchList call
                // Global list returns a Spring Page — rows live in data.content; orgName comes as organizationName
                const raw = res?.data?.data;
                const pageRows: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.content) ? raw.content : [];
                const data: GlobalMagicSignRow[] = pageRows.map((r) =>
                    deriveSigningLinks({ ...r, orgName: r.orgName ?? r.organizationName }),
                );
                if (page === 0) {
                    setRows(data);
                } else {
                    setRows((prev) => [...prev, ...data]);
                }
                setHasMore(data.length === GLOBAL_MAGIC_SIGN_PAGE_SIZE);
                setPageNo(page);
            } catch (error) {
                if (token !== fetchRequestTokenRef.current) return;
                console.error('[GlobalMagicSign] Failed to load global Magic Sign documents', error);
                toast.error('Failed to load global Magic Sign documents.');
            } finally {
                if (token === fetchRequestTokenRef.current) setLoading(false);
            }
        },
        [pnoId],
    );

    const { configMeta, fetchConfigMeta, handleGenerateLinks, polusDocTypes, fetchPolusDocTypes } = useMagicSignShared<GlobalMagicSignRow>({
        pnoId,
        setRows,
        fetchList,
        activeFilters,
        // The global list's generateLinks DTO carries the backend's raw `organizationName` field —
        // fold it into `orgName` (the FE-domain field name) before deriveSigningLinks runs.
        mapRawRow: (raw) => ({ ...raw, orgName: raw.orgName ?? raw.organizationName }),
        // The generateLinks DTO does not carry org fields back — keep the org name/logo already
        // resolved on the row in state so the org column doesn't blank out after generating links.
        mergeRow: (updatedRow, previousRow) => ({
            ...updatedRow,
            orgName: updatedRow.orgName ?? previousRow.orgName,
            orgLogoUrl: previousRow.orgLogoUrl,
        }),
    });

    const hasFetchedRef = useRef(false);
    useEffect(() => {
        // Reset the fetch guard when the tab is deactivated so the next activation always
        // gets fresh data (e.g. a doc just added from the Magic Upload tab becomes visible).
        if (!isActive) {
            hasFetchedRef.current = false;
            return;
        }
        // pnoId arrives async from redux — don't latch the fetch guard until it is present
        if (hasFetchedRef.current || !pnoId) return;
        hasFetchedRef.current = true;
        fetchList(0, activeFilters);
        fetchConfigMeta();
        fetchPolusDocTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, pnoId, fetchList, fetchConfigMeta, fetchPolusDocTypes]);

    /**
     * Global-view orphan upload: mirrors useMagicSignLogic.handleFileUpload exactly,
     * except orgId is omitted so the backend creates an orphan row with "Select Company Name" picker.
     * mnemosyneOrgId = investorOrgId (PNO org) — mirrors how MagicUpload handles untagged files
     * when no company is selected (assesseeOrgId is undefined → saveDocMetaDataInDocService
     * receives no orgId header, mnemosyne uses the PNO org context).
     */
    const handleGlobalFileUpload = useCallback(
        async (files: File[]) => {
            if (!files?.length || !pnoId) return;
            const maxBytes = MAGIC_SIGN_MAX_FILE_SIZE_MB * 1024 * 1024;
            const investorOrgId = platformData?.investorOrgId ?? '';

            for (let i = 0; i < files.length; i++) {
                const original = files[i];
                if (!MAGIC_SIGN_ALLOWED_TYPES.includes(original.type)) {
                    toast.error(MAGIC_SIGN_PDF_ONLY_TOAST);
                    continue;
                }
                if (original.size > maxBytes) {
                    toast.error(`${original.name}: File exceeds ${MAGIC_SIGN_MAX_FILE_SIZE_MB}MB limit.`);
                    continue;
                }

                // Append a timestamp suffix so a cancel + re-upload of the same document is not
                // rejected by Mnemosyne as a duplicate.
                const uniqueName = buildUniqueUploadFileName(original.name);
                const file = new File([original], uniqueName, { type: original.type });

                const tempId = `magic_sign_global_temp_${Date.now()}_${i}`;
                uploadingRowsRef.current.add(tempId);
                setRows((prev) => [{ id: tempId, fileName: file.name, status: MagicSignStatus.UPLOADING } as GlobalMagicSignRow, ...prev]);

                try {
                    // Orphan/global upload: no orgId — mirrors MU's untagged upload path where
                    // assesseeOrgId is undefined and mnemosyne falls back to PNO org context.
                    const newRow = await uploadMagicSignDocument({
                        file,
                        pnoId,
                        orgId: undefined,
                        mnemosyneOrgId: investorOrgId,
                        stagingCategoryId,
                    });
                    uploadingRowsRef.current.delete(tempId);
                    // Hardening: if the create response already carries an org (e.g. Data Room/MU
                    // "Add to Magic Sign" now sends a known orgId — Item 3), fold the raw backend
                    // field name into orgName so it renders immediately without waiting for a refetch.
                    setRows((prev) =>
                        prev.map((r) =>
                            r.id === tempId
                                ? ({
                                      ...newRow,
                                      orgName: (newRow as GlobalMagicSignRow).orgName ?? (newRow as any).organizationName,
                                  } as GlobalMagicSignRow)
                                : r,
                        ),
                    );
                } catch (error) {
                    console.error('[GlobalMagicSign] Failed to upload document', error);
                    toast.error(`Failed to upload ${file.name}`);
                    uploadingRowsRef.current.delete(tempId);
                    setRows((prev) => prev.filter((r) => r.id !== tempId));
                }
            }
        },
        [pnoId, platformData, stagingCategoryId, setRows],
    );

    const handleSendReminder = useCallback(
        async (id: string) => {
            try {
                await sendMagicSignReminder({ id, logoUrl: platformData?.logoUrl });
                toast.success('Reminder sent to pending signatories.');
                fetchList(0, activeFilters);
            } catch (error) {
                console.error('[GlobalMagicSign] Failed to send reminder', error);
                toast.error(error instanceof Error && error.message ? error.message : 'Failed to send reminder.');
            }
        },
        [fetchList, activeFilters, platformData],
    );

    const handleAssociateCompany = useCallback(
        async (rowId: string, orgId: string) => {
            try {
                await associateMagicSignCompany({ id: rowId, orgId });
                fetchList(0);
            } catch (error) {
                console.error('[GlobalMagicSign] Failed to associate company', error);
                toast.error(error instanceof Error && error.message ? error.message : 'Failed to associate company.');
            }
        },
        [fetchList],
    );

    const autoGeneratingRef = useRef<Set<string>>(new Set());
    const handleMaybeAutoGenerate = useCallback(
        (row: GlobalMagicSignRow) => {
            if (!isReadyToGenerateLinks(row) || autoGeneratingRef.current.has(row.id)) return;
            autoGeneratingRef.current.add(row.id);
            handleGenerateLinks(row.id).finally(() => autoGeneratingRef.current.delete(row.id));
        },
        [handleGenerateLinks],
    );

    const {
        handleUpdateDocType,
        handleUpdateSignType,
        handleUpdateSignatories,
        handleCancelConfirm,
        handleViewAuditLog,
        handleDownloadDocument,
        handleCopySigningLinks,
        handleRowClick,
    } = useMagicSignCommonHandlers<GlobalMagicSignRow>({
        setRows,
        setAuditLogRowName,
        setAuditLogOpen,
        setAuditLogLoading,
        setAuditLogEvents,
        setCancelConfirmId,
        setDocIdToView,
        setDocNameToView,
        setActiveRowId,
        fetchList,
        onMaybeAutoGenerate: handleMaybeAutoGenerate,
    });

    const loadMoreRows = useCallback(() => {
        if (!loading && hasMore) {
            fetchList(pageNo + 1, activeFilters);
        }
    }, [loading, hasMore, pageNo, fetchList, activeFilters]);

    const handleFiltersChange = useCallback(
        (newFilters: MagicSignFilters) => {
            setActiveFilters(newFilters);
            fetchList(0, newFilters);
        },
        [fetchList],
    );

    // Remove the auto-applied "my requests" chip → show everyone's documents
    const clearUserFilter = useCallback(() => {
        const { requestedBy, ...rest } = activeFilters;
        setActiveFilters(rest);
        fetchList(0, rest);
    }, [activeFilters, fetchList]);

    // Remove a single value from a multi-select filter (mirrors Magic Upload's removePill).
    // When the last value of a key is removed, the key is dropped entirely.
    const removeFilterValue = useCallback(
        (key: string, value: string) => {
            const current = (activeFilters as Record<string, unknown>)[key];
            const next: MagicSignFilters = { ...activeFilters };
            if (Array.isArray(current)) {
                const remaining = (current as string[]).filter((v) => v !== value);
                if (remaining.length) {
                    (next as Record<string, unknown>)[key] = remaining;
                } else {
                    delete (next as Record<string, unknown>)[key];
                }
            } else {
                delete (next as Record<string, unknown>)[key];
            }
            setActiveFilters(next);
            fetchList(0, next);
        },
        [activeFilters, fetchList],
    );

    // Clear All — drop every active filter, including the auto-applied user filter
    // (mirrors Magic Upload's Clear All). Sort preferences are preserved.
    const clearAllFilters = useCallback(() => {
        const { sortBy, sortDir } = activeFilters;
        const next: MagicSignFilters = sortBy || sortDir ? { sortBy, sortDir } : {};
        setActiveFilters(next);
        fetchList(0, next);
    }, [activeFilters, fetchList]);

    const isUserFiltered = !!activeFilters.requestedBy?.length;

    return {
        activeTab,
        setActiveTab,
        rows,
        loading,
        hasMore,
        configMeta,
        polusDocTypes,
        activeRowId,
        setActiveRowId,
        cancelConfirmId,
        setCancelConfirmId,
        auditLogOpen,
        setAuditLogOpen,
        auditLogEvents,
        auditLogLoading,
        auditLogRowName,
        docIdToView,
        setDocIdToView,
        docNameToView,
        activeFilters,
        handleFiltersChange,
        userName,
        currentUserId,
        isUserFiltered,
        clearUserFilter,
        removeFilterValue,
        clearAllFilters,
        handleAssociateCompany,
        handleGlobalFileUpload,
        handleUpdateDocType,
        handleUpdateSignType,
        handleUpdateSignatories,
        handleCancelConfirm,
        handleViewAuditLog,
        handleDownloadDocument,
        handleCopySigningLinks,
        handleRowClick,
        handleSendReminder,
        handleGenerateLinks,
        loadMoreRows,
        refreshList: useCallback(() => fetchList(0, activeFilters), [fetchList, activeFilters]),
    };
};

export default useGlobalMagicSignLogic;
